<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-box-seam me-2"></i>Product Details
      </h4>
      <div>
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading product details...</p>
      </div>

      <!-- Product Content -->
      <div v-if="!loading" class="product-details-content fade-in">
        <!-- Product Header -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-primary text-white py-3">
            <h5 class="mb-0"><i class="bi bi-box-seam me-2"></i>Product Overview</h5>
          </div>
          <div class="card-body">
            <div class="row align-items-center">
              <!-- Product Image Column -->
              <div class="col-md-3 text-center mb-3 mb-md-0">
                <div class="avatar-container">
                  <div class="avatar-wrapper">
                    <img
                      :src="product.avatar ?? 'https://chrisdellasega.com/wp-content/uploads/2023/03/image-placeholder.jpg'"
                      class="product-avatar rounded shadow-sm"
                      alt="Product Image"
                    />
                    <div class="avatar-overlay" v-if="profile.role !== 'user' && profile.role !== 'admin'">
                      <button
                        class="btn btn-sm btn-light"
                        @click="$refs.avatarInputEl.click()"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                    </div>
                  </div>
<!--                  <button-->
<!--                    v-if="profile.role !== 'user' && profile.role !== 'admin'"-->
<!--                    class="btn btn-sm btn-primary mt-2"-->
<!--                    @click="$refs.avatarInputEl.click()"-->
<!--                  >-->
<!--                    <i class="bi bi-upload me-1"></i>Change Image-->
<!--                  </button>-->
                  <input
                    ref="avatarInputEl"
                    type="file"
                    name="avatar"
                    accept=".jpeg,.png,.jpg,.webp"
                    hidden
                    @change="changeAvatar"
                  >
                  <ImageCropper
                    :open="cropper.open"
                    :file="cropper.file"
                    :aspect-ratio="cropPreset.aspectRatio"
                    :output-width="cropPreset.width"
                    :output-height="cropPreset.height"
                    title="Crop product image"
                    hint="Product images are shown as squares in the online shop and POS."
                    @cropped="uploadAvatar"
                    @closed="cropper.open = false"
                  />
                </div>
              </div>

              <!-- Product Details Column -->
              <div class="col-md-6">
                <h2 class="product-title mb-3">{{ product.name }}</h2>

                <div class="d-flex flex-wrap gap-2 mb-3">
                  <Badge pill :type="getPillByStatus(product.status)" class="me-2">
                    <i class="bi bi-circle-fill me-1 small"></i>{{ product.status }}
                  </Badge>
                  <Badge pill :type="getPillByReorder(product.reorder < product.stock ? 'good' : 'reorder')">
                    <i class="bi bi-graph-up me-1 small"></i>{{ product.reorder < product.stock ? 'Stock Good' : 'Reorder Needed' }}
                  </Badge>
                </div>

                <div class="d-flex flex-wrap align-items-center mb-3">
                  <div class="me-4 mb-2">
                    <span class="text-muted small d-block">Current Stock</span>
                    <span class="fw-bold">{{ product.stock + ' ' + product.unit.name }}</span>
                  </div>
                  <div class="me-4 mb-2">
                    <span class="text-muted small d-block">Selling Price</span>
                    <span class="fw-bold">{{ formatNumber(product.sell) }}</span>
                  </div>
                  <div class="me-4 mb-2">
                    <span class="text-muted small d-block">Category</span>
                    <span class="fw-bold">{{ product.category.name }}</span>
                  </div>
                  <div class="mb-2" v-if="product.brand">
                    <span class="text-muted small d-block">Brand</span>
                    <span class="fw-bold">{{ product.brand.name }}</span>
                  </div>
                </div>
              </div>

              <div class="col-md-3 text-center mb-3 mb-md-0">
                <div class="barcode-container mt-3 p-2 bg-white rounded text-center">
                  <img :src="product.barcode" width="150" alt="Product Barcode" class="barcode-image" />
                  <div class="mt-0">
                    <button v-print="'#printBarcode'" class="btn btn-sm btn-outline-primary">
                      <i class="bi bi-printer me-1"></i> Print Barcode
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Basic Information Section -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-light py-3">
            <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">SKU</h6>
                  <p class="info-value">{{ product.sku }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Code</h6>
                  <p class="info-value">{{ product.code }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Tags</h6>
                  <p v-if="displayTags.length > 0" class="info-value">
                    <span v-for="tag in displayTags" :key="tag" class="badge bg-info me-1">
                      {{ tag }}
                    </span>
                  </p>
                  <p v-else class="info-value">No tags available</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Description</h6>
                  <p class="info-value">{{ product.description || 'No description available' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Inventory & Pricing Section -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-light py-3">
            <h5 class="mb-0"><i class="bi bi-currency-dollar me-2"></i>Inventory & Pricing</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Current Stock</h6>
                  <p class="info-value">{{ product.stock + ' ' + product.unit.name }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Reorder Level</h6>
                  <p class="info-value">{{ product.reorder + ' ' + product.unit.name }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Buying Price</h6>
                  <p class="info-value">{{ formatNumber(product.buy) }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Selling Price</h6>
                  <p class="info-value">{{ formatNumber(product.sell) }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Expected Margin</h6>
                  <p class="info-value">
                    <span class="badge bg-info">{{ product.category.sale_margin_percentage }}%</span>
                  </p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Actual Margin</h6>
                  <p class="info-value">
                    <span class="badge" :class="getMarginBadgeClass()">
                      {{ calculateActualMargin() }}%
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Classification Section -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-light py-3">
            <h5 class="mb-0"><i class="bi bi-diagram-3 me-2"></i>Classification & Relationships</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6 col-lg">
                <div class="info-group">
                  <h6 class="info-label">Category</h6>
                  <p class="info-value">{{ product.category.name }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg">
                <div class="info-group">
                  <h6 class="info-label">Brand</h6>
                  <p class="info-value">{{ product.brand ? product.brand.name : 'No brand' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg">
                <div class="info-group">
                  <h6 class="info-label">Supplier</h6>
                  <p class="info-value">{{ product.supplier.name }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg">
                <div class="info-group">
                  <h6 class="info-label">Unit</h6>
                  <p class="info-value">{{ product.unit.name }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg">
                <div class="info-group">
                  <h6 class="info-label">Tax Category</h6>
                  <p class="info-value">{{ product.tax_category.description + ' - ' + product.tax_category.rate }}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-center gap-3 mt-4 mb-3">
          <button class="btn btn-outline-secondary px-4" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
          <button
            v-if="profile.role !== 'user' && product.status !== 'discontinued'"
            class="btn btn-outline-warning px-4"
            @click="toggleDiscontinue('discontinued')">
            <i class="bi bi-pause-circle me-1"></i> Discontinue Product
          </button>
          <button
            v-if="profile.role !== 'user' && product.status === 'discontinued'"
            class="btn btn-outline-success px-4"
            @click="toggleDiscontinue('active')">
            <i class="bi bi-play-circle me-1"></i> Reactivate Product
          </button>
          <button
            v-if="profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-primary px-4"
            @click="$router.push({path: '/edit-product/' + $route.params.id})">
            <i class="bi bi-pencil me-1"></i> Edit Product
          </button>
        </div>
      </div>
    </div>

    <!-- Hidden Barcode for Printing -->
    <div v-if="!loading" class="d-none">
      <div id="printBarcode">
        <div class="print-barcode-container">
          <h3 class="product-name">{{ product.name }}</h3>
          <img :src="product.barcode" alt="Product Barcode" class="print-barcode-image" />
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import productService from "@/api/productService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import formatNumber from "../../utils/formatNumber";
import Badge from "@/components/badge/Badge";
import print from 'vue3-print-nb';
import { CROP_PRESETS } from "@/utils/cropPresets";

export default {
  name: "ProductDetails",
  components: {
    Badge
  },
  directives: {
    print
  },

  data() {
    return {
      profile: {},
      loading: true,
      showModal: false,
      product: {},
      imagesUrl: process.env.VUE_APP_API_IMAGE_URL + 'product/',
      cropper: { open: false, file: null },
      cropPreset: CROP_PRESETS.product,
    }
  },

  methods: {
    formatNumber,
    formatDate,
    async getProduct() {
      this.product = await productService.getProduct(this.$route.params.id)
      this.loading = false
    },
    getPillByStatus(status) {
      switch (status) {
        case 'active':
          return 'success';
        case 'inactive':
          return 'secondary';
        case 'discontinued':
          return 'warning';
        default:
          return 'info';
      }
    },
    async toggleDiscontinue(newStatus) {
      const actionText = newStatus === 'discontinued' ? 'discontinue' : 'reactivate';
      const confirmed = window.confirm(`Are you sure you want to ${actionText} "${this.product.name}"?`);
      if (!confirmed) return;

      try {
        await productService.updateProductStatus(this.product.id, newStatus);
        this.product.status = newStatus;
      } catch (error) {
        console.error(`Error updating product status:`, error);
      }
    },
    getPillByReorder(status) {
      return status === 'good' ? 'success' : 'danger';
    },
    calculateActualMargin() {
      if (!this.product.buy || this.product.buy === 0) return 0;
      const margin = ((this.product.sell - this.product.buy) / this.product.buy) * 100;
      return margin.toFixed(2);
    },
    getMarginBadgeClass() {
      const actualMargin = parseFloat(this.calculateActualMargin());
      const expectedMargin = parseFloat(this.product.category?.sale_margin_percentage || 0);
      
      if (actualMargin < expectedMargin - 1) {
        return 'bg-danger';
      } else if (Math.abs(actualMargin - expectedMargin) <= 1) {
        return 'bg-success';
      } else {
        return 'bg-warning';
      }
    },
    changeAvatar(event) {
      const { files } = event.target
      if (files && files.length) {
        this.cropper.file = files[0]
        this.cropper.open = true
      }
      event.target.value = ''
    },
    async uploadAvatar(dataUrl) {
      const response = await productService.updateProductAvatar({
        avatar: dataUrl,
        product_id: this.product.id
      })
      if (response)
        await this.getProduct()
    },
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getProduct()
  },

  computed: {
    displayTags() {
      if (this.product.tags !== null) {
        return this.product.tags.split(',');
      } else {
        return [];
      }
    }
  }
}
</script>

<style scoped>
/* Product details styles */
.product-details-content {
  padding: 0.5rem;
}

.product-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

.info-group {
  margin-bottom: 0.5rem;
}

.info-label {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  color: #555;
}

.info-value {
  font-size: 1rem;
  color: #333;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.card {
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05) !important;
}

/* Product header styles */
.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-wrapper {
  position: relative;
  max-width: 200px;
  max-height: 200px;
  margin: 0 auto;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.product-avatar {
  max-width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
  border: 1px solid #eee;
  padding: 4px;
  background-color: white;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
}

.barcode-container {
  border: 1px solid #ffffff;
}

.barcode-image {
  max-width: 100%;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Barcode Print Styles */
.print-barcode-container {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  font-family: Arial, sans-serif;
}

.print-barcode-image {
  width: 100%;
  max-width: 250px;
  margin: 15px auto;
}

.product-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
}

.product-info {
  font-size: 14px;
  margin: 5px 0;
}

/* Print Media Query */
@media print {
  @page {
    size: 3in 2in;
    margin: 0;
  }

  body * {
    visibility: hidden;
  }

  #printBarcode, #printBarcode * {
    visibility: visible;
  }

  #printBarcode {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .info-label {
    font-size: 0.85rem;
  }

  .info-value {
    font-size: 0.95rem;
  }

  .product-title {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }

  .product-avatar {
    max-height: 150px;
    margin-bottom: 1rem;
  }

  .barcode-container {
    margin-top: 1rem;
  }

  /* Improve spacing in the flex containers on mobile */
  .d-flex.flex-wrap {
    justify-content: space-between;
  }
}
</style>
